// SPDX-License-Identifier: AGPL-3.0-or-later

import {OAuthAuthorizeModal} from '@app/features/auth/components/modals/OAuthAuthorizeModal';
import styles from '@app/features/discovery/apps/AppDiscoveryCard.module.css';
import {AppDetailModal} from '@app/features/discovery/components/modals/AppDetailModal';
import type {DiscoveryApp} from '@app/features/discovery/state/AppDiscoveryState';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {CheckCircleIcon, PlusIcon, ShieldCheckIcon, UsersThreeIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface AppDiscoveryCardProps {
	readonly app: DiscoveryApp;
}

export const AppDiscoveryCard: React.FC<AppDiscoveryCardProps> = observer(({app}) => {
	const handleOpenDetail = useCallback(() => {
		ModalCommands.pushWithKey(
			modal(() => <AppDetailModal app={app} />),
			`app-detail-${app.id}`,
		);
	}, [app]);

	const handleAddServer = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			if (app.customInstallUrl) {
				window.open(app.customInstallUrl, '_blank', 'noopener,noreferrer');
				return;
			}
			ModalCommands.pushWithKey(
				modal(() => (
					<OAuthAuthorizeModal
						search={`?client_id=${app.clientId}&scope=bot%20applications.commands&permissions=${app.permissions || '8'}`}
						data-flx="discovery.apps.oauth-modal"
					/>
				)),
				`oauth-authorize:${app.clientId}`,
			);
		},
		[app],
	);

	return (
		<div
			className={styles.card}
			onClick={handleOpenDetail}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					handleOpenDetail();
				}
			}}
			data-flx={`discovery.apps.card.${app.id}`}
		>
			<div
				className={styles.banner}
				style={{
					background: app.banner || 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
				}}
			/>

			<div className={styles.content}>
				<div className={styles.topRow}>
					<div className={styles.avatarWrapper}>
						<img src={app.avatar} alt={app.name} className={styles.avatar} />
						{app.verified && (
							<div className={styles.verifiedCheck} title="Verified Application">
								<CheckCircleIcon size={16} weight="fill" color="#5865f2" />
							</div>
						)}
					</div>
					<span className={styles.categoryPill}>{app.category}</span>
				</div>

				<div className={styles.nameRow}>
					<h3 className={styles.name}>{app.name}</h3>
					{app.verified && <ShieldCheckIcon size={18} weight="fill" color="#5865f2" />}
				</div>

				<p className={styles.tagline}>{app.tagline}</p>

				<div className={styles.footer}>
					<div className={styles.serverCount}>
						<UsersThreeIcon size={15} />
						<span>{app.stats.serverCount.toLocaleString()} servers</span>
					</div>

					<button
						type="button"
						className={styles.addBtn}
						onClick={handleAddServer}
						data-flx={`discovery.apps.card.add-btn.${app.id}`}
					>
						<PlusIcon size={14} weight="bold" />
						<span>Add</span>
					</button>
				</div>
			</div>
		</div>
	);
});
